import type React from 'react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Modal } from '../../../../../customComponents/Modal'
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material'
import { AddFocusBuilding, UpdateFocusBuilding } from '../../../../../../services/AnnualPlanService'
import { FocusBuildingPlanType } from '../../../../../../models/FocusBuildingPlanType'
import { ActivityEditSession } from '../../ActivityEditSession'
import { PriorityFourActivityName } from '../../../../../../models/PriorityFourActivityName'
import {
  useEEMDistrictAndBuildingsOptions
} from '../../../../../../hooks/use-eemDistrictBuildingOptions'
import { OrganizationType } from '../../../../../../models/OrganizationType'
import {
  useEEMOrganizationBuildingOptions
} from '../../../../../../hooks/use-eemOrganizationBuildingOptions'
import { type FocusBuilding } from '../../../../../../models/FocusBuilding'
import { type AddFocusBuildingRequest } from '../../../../../../models/requestObjects/priorityFour/focusBuilding/FocusBuildingRequestObjects'
import { BudgetThresholdExceededModal } from '../../../BudgetExceededModal'
import { isValidPositiveNumber } from '../../../../../../utils/isValidNumber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

export interface AddFocusBuildingFormValues {
  priorityId: number
  organizationType: OrganizationType | null
  organizationId: number | null
  organizationContactName: string
  organizationContactEmail: string
  buildingId: number | null
  buildingContactName: string
  buildingContactEmail: string
  planType: string | null
  actions: string
  resources: string
  budgetRequest: number | null
}

interface Props {
  open: boolean
  initialValues?: FocusBuilding
  priorityId: number
  planOrgId: number
  isdOrgId: number
  implementationPhase: boolean
  onClose: () => void
  onSaved: () => void
}

type ConfirmState = 'none' | 'needsConfirmation' | 'confirmed'

export const AddOrEditFocusBuildingsModal = (props: Props): JSX.Element => {
  const [formValues, setFormValues] = useState<AddFocusBuildingFormValues>({
    priorityId: props?.priorityId ?? 0,
    organizationType: null,
    organizationId: null,
    organizationContactName: '',
    organizationContactEmail: '',
    buildingId: null,
    buildingContactName: '',
    buildingContactEmail: '',
    planType: null,
    actions: '',
    resources: '',
    budgetRequest: null
  })
  const [confirmState, setConfirmState] = useState<ConfirmState>('none')
  const [submitted, setIsSubmitted] = useState(false)
  const [overBudget, setOverBudget] = useState(false)
  const { districtAndBuildingsOptions, isLoading: isLoadingPlanOrgDistricts } = useEEMDistrictAndBuildingsOptions(props.planOrgId)
  const { buildings: isdBuildings, isLoading: isLoadingIsdBuildings } = useEEMOrganizationBuildingOptions(props.isdOrgId)
  const startingBudget = props.initialValues?.budgetRequest

  const isEditing = props.initialValues != null

  useEffect(() => {
    if (props.initialValues != null) {
      setFormValues({
        priorityId: props.initialValues.priorityId,
        organizationType: props.initialValues.organizationType,
        organizationId: props.initialValues.organization.id,
        actions: props.initialValues.actions,
        budgetRequest: props.initialValues?.adjustedBudget != null ? props.initialValues.adjustedBudget : props.initialValues.budgetRequest,
        buildingContactEmail: props.initialValues.buildingContactEmail,
        buildingContactName: props.initialValues.buildingContactName,
        buildingId: props.initialValues.building.id,
        organizationContactEmail: props.initialValues.organizationContactEmail,
        organizationContactName: props.initialValues.organizationContactName,
        planType: props.initialValues.planType,
        resources: props.initialValues.planType
      })
    }
  }, [props.initialValues])

  useEffect(() => {
    if (!props.implementationPhase || startingBudget == null) return
    if (formValues.budgetRequest != null && formValues.budgetRequest > startingBudget * 1.1) {
      setOverBudget(true)
    }
  }, [formValues.budgetRequest])

  const validForm = (values: AddFocusBuildingFormValues): values is AddFocusBuildingRequest => {
    return !(
      values.organizationType == null ||
      values.organizationId == null ||
      values.buildingId == null ||
      values.organizationContactName === '' ||
      values.organizationContactEmail === '' ||
      values.buildingContactName === '' ||
      values.buildingContactEmail === '' ||
      values.planType == null ||
      values.actions === '' ||
      values.resources === '' ||
      !isValidPositiveNumber(values.budgetRequest)
    )
  }

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    const partialUpdates: Partial<AddFocusBuildingFormValues> = {}

    if (name === 'organizationType') {
      switch (value) {
        case OrganizationType.Isd:
          partialUpdates.organizationId = props.isdOrgId
          partialUpdates.buildingId = null
          break
        case OrganizationType.District:
          partialUpdates.organizationId = null
          partialUpdates.buildingId = null
          break
      }
    }

    setFormValues({ ...formValues, [name]: value, ...partialUpdates })
  }

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitted(true)
    if (!validForm(formValues)) {
      toast.error('Please complete all fields')
      return
    }

    if (overBudget && confirmState === 'none') {
      setConfirmState('needsConfirmation')
      return
    }

    const { success, errors } = props.initialValues != null
      ? await UpdateFocusBuilding({
        id: props.initialValues.id,
        priorityId: props.priorityId,
        organizationType: formValues.organizationType ?? undefined,
        organizationId: formValues.organizationId ?? undefined,
        organizationContactName: formValues.organizationContactName ?? undefined,
        organizationContactEmail: formValues.organizationContactEmail ?? undefined,
        buildingId: formValues.buildingId ?? undefined,
        buildingContactName: formValues.buildingContactName ?? undefined,
        buildingContactEmail: formValues.buildingContactEmail ?? undefined,
        planType: formValues.planType ?? undefined,
        actions: formValues.actions ?? undefined,
        resources: formValues.resources ?? undefined,
        budgetRequest: formValues.budgetRequest ?? undefined,
        isUpdate: props.initialValues.isUpdate,
        isImplementationPhase: props.implementationPhase,
        needsReview: overBudget
      })
      : await AddFocusBuilding({
        ...formValues,
        isImplementationPhase: props.implementationPhase
      })

    if (!success) {
      errors.forEach((error) => toast.error(error))
      return
    }

    toast.success('Focus Building Saved')

    props.onClose()
    props.onSaved()
  }

  const isDistrict = formValues.organizationType === OrganizationType.District
  const selectedDistrict = districtAndBuildingsOptions.find(d => d.id.toString() === formValues.organizationId?.toString())
  const buildings = formValues.organizationType == null
    ? []
    : isDistrict
      ? (selectedDistrict?.buildings ?? [])
      : isdBuildings

  return (confirmState === 'needsConfirmation'
    ? <BudgetThresholdExceededModal
        onClose={() => {
          setConfirmState('none')
          props.onClose()
        }}
        onConfirm={() => {
          setConfirmState('confirmed')
          void handleSubmit()
        }}
    />
    : <Modal
        title='Focus Buildings'
        open={props.open}
        data-testid={props?.initialValues != null ? 'editFocusBuildingModal' : 'addFocusBuildingModal'}
        width='480px'
        confirmButtonText='Save'
        cancelButtonText='Cancel'
        onClose={props.onClose}
        onConfirm={handleSubmit}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {
              props.implementationPhase && props.initialValues == null &&
                <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
                  backgroundColor: 'var(--blue-500)',
                  color: 'white',
                  mb: '.5em',
                  fill: 'white'
                }} severity="info"
                >The addition of this item will prompt a review from the START team.
                </Alert>
            }
            <Typography variant='h5'>Location</Typography>
            <FormControl
              fullWidth
              data-testid='organizationType'
              disabled={!props.implementationPhase && isEditing}
              required={formValues.organizationType == null}
              error={submitted && formValues.organizationType == null}
            >
              <RadioGroup
                row
                defaultValue={formValues.organizationType}
                name='organizationType'
                onChange={handleFieldUpdate}
                value={formValues.organizationType}
                sx={{ display: 'flex', gap: '8px' }}
              >
                <FormLabel sx={{ alignContent: 'center' }}>Is this for an ISD or a District?</FormLabel>
                <Box>
                  {Object.values(OrganizationType).map((orgType) => (
                    <FormControlLabel
                      key={orgType}
                      value={orgType ?? ''}
                      label={orgType}
                      control={<Radio />}
                      data-testid={`organizationType${orgType}`}
                    />
                  ))}
                </Box>
              </RadioGroup>
            </FormControl>

            <Typography variant='h5'>{formValues.organizationType}</Typography>

            {isDistrict && (
              <FormControl
                fullWidth
                disabled={!props.implementationPhase && isEditing}
                required={formValues.organizationId == null}
                error={submitted && formValues.organizationId == null}
              >
                <InputLabel id="district-label">Select District</InputLabel>
                <Select
                  labelId="district-label"
                  id="organizationId"
                  value={districtAndBuildingsOptions.some(d => d.id === formValues.organizationId) ? formValues.organizationId : ''}
                  label="Select District"
                  name='organizationId'
                  data-testid='district'
                  onChange={handleFieldUpdate as (e: SelectChangeEvent<typeof formValues.organizationId>) => void}
                  disabled={!props.implementationPhase && isEditing}
                >
                  {isLoadingPlanOrgDistricts && <MenuItem disabled>Loading districts...</MenuItem>}
                  {districtAndBuildingsOptions.length === 0 && <MenuItem disabled>No district options</MenuItem>}
                  {districtAndBuildingsOptions.map((district) => (
                    <MenuItem key={district.id} value={district.id} data-testid='districtOption'>{district.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              name='organizationContactName'
              label={`${formValues.organizationType ?? ''} Contact Name`}
              value={formValues.organizationContactName}
              data-testid='organizationContactName'
              onChange={handleFieldUpdate}
              variant='filled'
              required={formValues.organizationContactName === ''}
              error={submitted && formValues.organizationContactName === ''}
            />

            <TextField
              name='organizationContactEmail'
              label={`${formValues.organizationType ?? ''} Contact Email`}
              value={formValues.organizationContactEmail}
              data-testid='organizationContactEmail'
              onChange={handleFieldUpdate}
              variant='filled'
              required={formValues.organizationContactEmail === ''}
              error={submitted && formValues.organizationContactEmail === ''}
            />

            <Typography variant='h5'>Building Info</Typography>
            <FormControl
              fullWidth
              variant='filled'
              required={formValues.buildingId == null}
              error={submitted && formValues.buildingId == null}
            >
              <InputLabel id="building-label">Select Building</InputLabel>
              <Select
                labelId="building-label"
                id="building"
                value={buildings.length > 0 && formValues.buildingId != null ? formValues.buildingId : ''}
                label="Select Building"
                name='buildingId'
                data-testid='building'
                onChange={handleFieldUpdate as (e: SelectChangeEvent<typeof formValues.buildingId>) => void}
                disabled={!props.implementationPhase && isEditing}
              >
                {!isDistrict && isLoadingIsdBuildings && <MenuItem disabled>Loading buildings...</MenuItem>}
                {formValues.organizationType == null && <MenuItem disabled>Select an organization type for building options</MenuItem>}
                {isDistrict && formValues.organizationId == null && <MenuItem disabled>Select a district for building options</MenuItem>}
                {(formValues.organizationId != null && formValues.organizationType != null) && buildings.length === 0 && <Typography variant='body1' textAlign={'center'}>No building options</Typography>}
                {buildings.length > 0 && buildings.map((building) => (
                  <MenuItem key={building.id} value={building.id} data-testid='buildingOption'>{building.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              variant='filled'
              name='buildingContactName'
              label='Building Contact Name'
              value={formValues.buildingContactName}
              data-testid='buildingContactName'
              onChange={handleFieldUpdate}
              required={formValues.buildingContactName === ''}
              error={submitted && formValues.buildingContactName === ''}
            />

            <TextField
              variant='filled'
              name='buildingContactEmail'
              label='Building Contact Email'
              value={formValues.buildingContactEmail}
              data-testid='buildingContactEmail'
              onChange={handleFieldUpdate}
              required={formValues.buildingContactEmail === ''}
              error={submitted && formValues.buildingContactEmail === ''}
            />

            <FormControl
              data-testid='planType'
              required={formValues.planType == null}
              error={submitted && formValues.planType == null}
            >
              <FormLabel>What type of plan will this be?</FormLabel>
              <RadioGroup
                aria-labelledby="planType-radio-buttons-group"
                name="planType"
                value={formValues.planType ?? ''}
                onChange={handleFieldUpdate}
              >
                <FormControlLabel data-testid='planTypeNew' value={FocusBuildingPlanType.New} control={<Radio />} label="Establishing new program" />
                <FormControlLabel data-testid='planTypeExpanding' value={FocusBuildingPlanType.Expanding} control={<Radio />} label="Expanding existing program" />
              </RadioGroup>
            </FormControl>

            <TextField
              name='actions'
              label={<>What <span style={{ fontWeight: 700 }}>actions</span> are planned?</>}
              value={formValues.actions}
              data-testid='actions'
              onChange={handleFieldUpdate}
              multiline={true}
              rows={4}
              variant='filled'
              required={formValues.actions === ''}
              error={submitted && formValues.actions === ''}
            />

            <TextField
              name='resources'
              label={<>What <span style={{ fontWeight: 700 }}>resources</span> are needed?</>}
              value={formValues.resources}
              data-testid='resources'
              onChange={handleFieldUpdate}
              variant='filled'
              multiline={true}
              rows={4}
              required={formValues.resources === ''}
              error={submitted && formValues.resources === ''}
            />

            <Typography variant='body1'>Does this require additional funding to complete?</Typography>

            <TextField
              id='budget-request'
              sx={{ mt: '.75em', width: '250px' }}
              variant='filled'
              onChange={handleFieldUpdate}
              label={'Budget Request'}
              data-testid='budgetRequest'
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              name='budgetRequest'
              required
              error={submitted && !isValidPositiveNumber(formValues.budgetRequest)}
              value={formValues.budgetRequest ?? ''}
            />

            {props.initialValues != null &&
              <ActivityEditSession
                activityId={props.initialValues.id}
                activityName={PriorityFourActivityName.FocusBuildings}
                onClose={props.onClose}
              />
            }
          </Box>
    }
    />)
}
